import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { DealerService } from '../../../core/services/dealer.service';
import { Dealer } from '../../../core/models/dealer.model';
import { ServiceAdvisorRequest } from '../../../core/models/service-advisor.model';

@Component({
  selector: 'app-dealer-list',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './dealer-list.html',
  styleUrl: './dealer-list.scss',
})
export class DealerList {
  private readonly dealerService = inject(DealerService);
  private readonly fb = inject(FormBuilder);

  protected readonly dealers = signal<Dealer[]>([]);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);

  protected readonly page = signal(1);
  protected readonly pageSize = signal(25);
  protected readonly totalCount = signal(0);
  protected readonly overallTotalCount = signal(0);
  protected readonly totalPages = computed(() => Math.max(1, Math.ceil(this.totalCount() / this.pageSize())));

  protected readonly searchTerm = signal('');
  protected readonly stateFilter = signal('');
  protected readonly cityFilter = signal('');
  protected readonly customerStatusFilter = signal('');
  protected readonly productFilter = signal('');
  protected readonly csatFilter = signal('');
  protected readonly driveOverVersionFilter = signal('');
  private searchDebounceHandle?: ReturnType<typeof setTimeout>;

  protected readonly customerStatusOptions = ['Active Customer', 'At Risk', 'Pending Cancellation', 'Cancelled'];
  protected readonly productOptions: { value: string; label: string }[] = [
    { value: 'DriveOver', label: 'Drive Over' },
    { value: 'Handheld', label: 'Handheld' },
    { value: 'OtherProducts', label: 'Other Products' },
  ];
  protected readonly csatOptions = ['Advocate/Reference', 'Good Standing', 'At Risk'];

  protected readonly driveOverVersionOptions = ['TSX X', 'TSX 1.0', 'TSX 2.0', 'TSX 2.5'];

  protected readonly hasActiveFilters = computed(() =>
    !!this.searchTerm() || !!this.stateFilter() || !!this.cityFilter() ||
    !!this.customerStatusFilter() || !!this.productFilter() || !!this.csatFilter() || !!this.driveOverVersionFilter()
  );

  protected readonly modalOpen = signal(false);
  protected readonly saving = signal(false);
  protected readonly editingDealer = signal<Dealer | null>(null);

  protected readonly serviceAdvisors = signal<ServiceAdvisorRequest[]>([]);
  protected readonly advisorsLoading = signal(false);

  protected readonly syncing = signal(false);
  protected readonly syncMessage = signal<string | null>(null);

  protected readonly dealerForm = this.fb.nonNullable.group({
    hubspotDealerId: [''],
    dealerName: ['', Validators.required],
    email: [''],
    streetName: [''],
    city: [''],
    state: [''],
    zipCode: [''],
    latitude: [''],
    longitude: [''],
    supervisorName: [''],
    phNo: [''],
    laneCount: [''],
    csatRating: [''],
    customerStatus: [''],
    traxtionDevice: [''],
    serviceAdvisorFirstName: [''],
    serviceAdvisorLastName: [''],
    serviceAdvisorEmail: [''],
  });

  private static readonly LOCKED_ON_EDIT_FIELDS = [
    'hubspotDealerId', 'dealerName', 'email', 'streetName', 'city', 'state', 'zipCode',
    'supervisorName', 'phNo', 'laneCount', 'csatRating', 'customerStatus', 'traxtionDevice',
  ] as const;

  protected readonly isHubspotDealer = computed(() => !!this.editingDealer()?.hubspotDealerId);

  constructor() {
    this.loadDealers();
    this.loadOverallTotal();
  }

  private loadDealers(): void {
    this.loading.set(true);
    this.error.set(null);
    this.dealerService.getDealers({
      page: this.page(),
      pageSize: this.pageSize(),
      search: this.searchTerm() || undefined,
      state: this.stateFilter() || undefined,
      city: this.cityFilter() || undefined,
      customerStatus: this.customerStatusFilter() || undefined,
      product: this.productFilter() || undefined,
      csatRating: this.csatFilter() || undefined,
      driveOverVersion: this.driveOverVersionFilter() || undefined,
    }).subscribe({
      next: (result) => {
        this.dealers.set(result.items);
        this.totalCount.set(result.totalCount);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set('Failed to load dealers. Is the FieldService API running?');
        this.loading.set(false);
        console.error(err);
      }
    });
  }

  private loadOverallTotal(): void {
    this.dealerService.getDealers({ page: 1, pageSize: 1 }).subscribe({
      next: (result) => this.overallTotalCount.set(result.totalCount),
      error: (err) => console.error(err)
    });
  }

  protected onSearchInput(value: string): void {
    this.searchTerm.set(value);
    clearTimeout(this.searchDebounceHandle);
    this.searchDebounceHandle = setTimeout(() => {
      this.page.set(1);
      this.loadDealers();
    }, 400);
  }

  protected onStateFilterChange(value: string): void {
    this.stateFilter.set(value.trim());
    this.page.set(1);
    this.loadDealers();
  }

  protected onCityFilterChange(value: string): void {
    this.cityFilter.set(value.trim());
    this.page.set(1);
    this.loadDealers();
  }

  protected onCustomerStatusFilterChange(value: string): void {
    this.customerStatusFilter.set(value);
    this.page.set(1);
    this.loadDealers();
  }

  protected onProductFilterChange(value: string): void {
    this.productFilter.set(value);
    this.page.set(1);
    this.loadDealers();
  }

  protected onCsatFilterChange(value: string): void {
    this.csatFilter.set(value);
    this.page.set(1);
    this.loadDealers();
  }

  protected onDriveOverVersionFilterChange(value: string): void {
    this.driveOverVersionFilter.set(value);
    this.page.set(1);
    this.loadDealers();
  }

  protected clearFilters(): void {
    clearTimeout(this.searchDebounceHandle);
    this.searchTerm.set('');
    this.stateFilter.set('');
    this.cityFilter.set('');
    this.customerStatusFilter.set('');
    this.productFilter.set('');
    this.csatFilter.set('');
    this.driveOverVersionFilter.set('');
    this.page.set(1);
    this.loadDealers();
  }

  protected goToPreviousPage(): void {
    if (this.page() > 1) {
      this.page.update((p) => p - 1);
      this.loadDealers();
    }
  }

  protected goToNextPage(): void {
    if (this.page() < this.totalPages()) {
      this.page.update((p) => p + 1);
      this.loadDealers();
    }
  }

  protected syncWithHubspot(): void {
    this.syncing.set(true);
    this.syncMessage.set(null);
    this.dealerService.syncDealers().subscribe({
      next: (result) => {
        this.syncing.set(false);
        this.syncMessage.set(`Synced ${result.synced} of ${result.total} dealers.`);
        this.loadDealers();
        this.loadOverallTotal();
      },
      error: (err) => {
        this.syncing.set(false);
        this.syncMessage.set('Sync failed. Check the API logs.');
        console.error(err);
      }
    });
  }

  private applyEditLock(locked: boolean): void {
    for (const field of DealerList.LOCKED_ON_EDIT_FIELDS) {
      const control = this.dealerForm.get(field);
      if (locked) control?.disable({ emitEvent: false });
      else control?.enable({ emitEvent: false });
    }
  }

  protected openAddModal(): void {
    this.editingDealer.set(null);
    this.dealerForm.reset();
    this.applyEditLock(false);
    this.serviceAdvisors.set([]);
    this.modalOpen.set(true);
  }

  protected openEditModal(dealer: Dealer): void {
    this.editingDealer.set(dealer);
    this.serviceAdvisors.set([]);
    if (dealer.dealerId) {
      this.advisorsLoading.set(true);
      this.dealerService.getServiceAdvisors(dealer.dealerId).subscribe({
        next: (result) => {
          this.serviceAdvisors.set(result.items.map((a) => ({
            firstName: a.firstName ?? '',
            lastName: a.lastName ?? '',
            email: a.email ?? '',
            phone: a.phone ?? '',
          })));
          this.advisorsLoading.set(false);
        },
        error: (err) => {
          this.advisorsLoading.set(false);
          console.error('Failed to load service advisors', err);
        }
      });
    }
    this.dealerForm.reset({
      hubspotDealerId: dealer.hubspotDealerId ?? '',
      dealerName: dealer.dealerName ?? '',
      email: dealer.email ?? '',
      streetName: dealer.streetName ?? '',
      city: dealer.city ?? '',
      state: dealer.state ?? '',
      zipCode: dealer.zipCode ?? '',
      latitude: dealer.latitude != null ? String(dealer.latitude) : '',
      longitude: dealer.longitude != null ? String(dealer.longitude) : '',
      supervisorName: dealer.supervisorName ?? '',
      phNo: dealer.phNo ?? '',
      laneCount: dealer.laneCount ?? '',
      csatRating: dealer.csatRating ?? '',
      customerStatus: dealer.customerStatus ?? '',
      traxtionDevice: dealer.traxtionDevice ?? '',
      serviceAdvisorFirstName: dealer.serviceAdvisorFirstName ?? '',
      serviceAdvisorLastName: dealer.serviceAdvisorLastName ?? '',
      serviceAdvisorEmail: dealer.serviceAdvisorEmail ?? '',
    });
    this.applyEditLock(true);
    this.modalOpen.set(true);
  }

  protected closeModal(): void {
    this.modalOpen.set(false);
  }

  protected saveDealer(): void {
    if (this.dealerForm.invalid) {
      this.dealerForm.markAllAsTouched();
      return;
    }

    const value = this.dealerForm.getRawValue();
    const editing = this.editingDealer();
    this.saving.set(true);

    const request = {
      ...value,
      latitude: value.latitude ? Number(value.latitude) : undefined,
      longitude: value.longitude ? Number(value.longitude) : undefined,
    };

    if (editing != null && editing.dealerId) {
      this.dealerService.updateDealer(editing.dealerId, { ...request, isActive: editing.isActive ?? true }).subscribe({
        next: (updated) => {
          this.dealers.update((list) => list.map((d) => (d.dealerId === updated.dealerId ? updated : d)));
          this.saveServiceAdvisorsFor(updated.dealerId);
        },
        error: (err) => {
          this.saving.set(false);
          this.error.set('Failed to save dealer.');
          console.error(err);
        }
      });
    } else {
      this.dealerService.createDealer(request).subscribe({
        next: (created) => {
          this.dealers.update((list) => [...list, created]);
          this.overallTotalCount.update((c) => c + 1);
          this.saveServiceAdvisorsFor(created.dealerId);
        },
        error: (err) => {
          this.saving.set(false);
          this.error.set('Failed to create dealer.');
          console.error(err);
        }
      });
    }
  }

  private saveServiceAdvisorsFor(dealerId: number): void {
    const advisors = this.serviceAdvisors().filter((a) => a.firstName || a.lastName || a.email);
    this.dealerService.saveServiceAdvisors(dealerId, advisors).subscribe({
      next: () => {
        this.saving.set(false);
        this.closeModal();
      },
      error: (err) => {
        this.saving.set(false);
        this.error.set('Dealer saved, but failed to save service advisors.');
        console.error(err);
      }
    });
  }

  protected addAdvisorLine(): void {
    this.serviceAdvisors.update((list) => [...list, { firstName: '', lastName: '', email: '', phone: '' }]);
  }

  protected removeAdvisorLine(index: number): void {
    this.serviceAdvisors.update((list) => list.filter((_, i) => i !== index));
  }

  protected updateAdvisorField(index: number, field: keyof ServiceAdvisorRequest, value: string): void {
    this.serviceAdvisors.update((list) => list.map((a, i) => (i === index ? { ...a, [field]: value } : a)));
  }

  protected deactivateDealer(dealer: Dealer): void {
    if (!dealer.dealerId) {
      return;
    }
    this.dealerService.deactivateDealer(dealer.dealerId).subscribe({
      next: () => {
        this.dealers.update((list) => list.map((d) => (d.dealerId === dealer.dealerId ? { ...d, isActive: false } : d)));
      },
      error: (err) => {
        this.error.set('Failed to deactivate dealer.');
        console.error(err);
      }
    });
  }

  protected sentimentColor(csatRating: string | null): string {
    switch (csatRating) {
      case 'Advocate/Reference': return '#8DC63F';
      case 'Good Standing': return '#00B9F2';
      case 'At Risk': return '#ef4444';
      default: return '#64748B';
    }
  }

  protected statusColor(status: string | null): string {
    switch (status) {
      case 'Active Customer': return '#8DC63F';
      case 'At Risk': return '#F29727';
      case 'Pending Cancellation': return '#f59e0b';
      case 'Cancelled': return '#ef4444';
      default: return '#64748B';
    }
  }
}
